import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { Box, StackView } from '@planningcenter/tapestry-react'

import Table from '../../shared/table'
import {
  Code,
  Heading,
  PageHeading,
  Link as SharedLink,
  Text,
} from '../../shared/typography'

const ValuesTable = () => {
  const columns = [
    {
      header: 'Value',
      cell: 'value',
      css: { flex: '1 1 0' },
    },
    {
      header: 'Format',
      cell: 'format',
      css: { flex: '1 1 0' },
    },
    {
      header: 'Example',
      cell: 'example',
      css: { flex: '1 1 0' },
    },
  ]

  const data = [
    {
      value: 'date',
      format: 'YYYY-MM-DD',
      example: '2015-05-05',
    },
    {
      value: 'date and time',
      format: 'YYYY-MM-DDTHH:MM:SSZ',
      example: '2015-05-05T22:40:07Z',
    },
  ]

  return <Table columns={columns} data={data} />
}

const OperatorsTable = () => {
  const columns = [
    {
      header: 'Operator',
      cell: row => <Code>{row.operator}</Code>,
      width: 80,
      css: { flex: '1 1 1' },
    },
    {
      header: 'Usage',
      cell: row => <Code>{row.usage}</Code>,
      css: { flex: '1 1 0' },
    },
    {
      header: 'Result',
      cell: 'result',
      css: { flex: '1 1 0' },
    },
  ]

  const data = [
    {
      operator: '=',
      usage: '?where[created_at]=2018-02-22',
      result: 'created on February 22nd, 2018',
    },
    {
      operator: '[gt]=',
      usage: '?where[created_at][gt]=2018-02-22',
      result: 'created after February 22nd, 2018',
    },
    {
      operator: '[gte]=',
      usage: '?where[created_at][gte]=2018-02-22',
      result: 'created on or after February 22nd, 2018',
    },
    {
      operator: '[lt]=',
      usage: '?where[created_at][lt]=2018-02-22',
      result: 'created before February 22nd, 2018',
    },
    {
      operator: '[lte]=',
      usage: '?where[created_at][lte]=2018-02-22',
      result: 'created on or before February 22nd, 2018',
    },
  ]

  return <Table columns={columns} data={data} />
}

const ConversionsTable = () => {
  const columns = [
    {
      header: 'Usage',
      cell: row => <Code>{row.usage}</Code>,
    },
    {
      header: 'Time zone',
      cell: 'time_zone',
    },
    {
      header: 'Result',
      cell: row => <Code>{row.result}</Code>,
      css: { flexGrow: '1' },
    },
  ]

  const data = [
    {
      usage: '?where[created_at]=2018-02-22',
      time_zone: 'Pacific Time (US & Canada)',
      result:
        '2018-02-22T08:00:00Z <= created_at <= 2018-02-23T07:59:59.999999Z',
    },
    {
      usage: '?where[created_at][gt]=2018-02-22',
      time_zone: 'Pacific Time (US & Canada)',
      result: 'created_at > 2018-02-23T07:59:59.999999Z',
    },
    {
      usage: '?where[created_at][gte]=2018-02-22',
      time_zone: 'Pacific Time (US & Canada)',
      result: 'created_at >= 2018-02-22T08:00:00Z',
    },
    {
      usage: '?where[created_at][lt]=2018-02-22',
      time_zone: 'Pacific Time (US & Canada)',
      result: 'created_at < 2018-02-22T08:00:00Z',
    },
    {
      usage: '?where[created_at][lte]=2018-02-22',
      time_zone: 'Pacific Time (US & Canada)',
      result: 'created_at <= 2018-02-23T07:59:59.999999Z',
    },
  ]

  return <Table columns={columns} data={data} />
}

const ExplicitTimesTable = () => {
  const columns = [
    {
      header: 'Usage',
      cell: row => (
        <Box>
          {row.usage.split('&').map((p, i) => {
            if (!p) return null
            return (
              <>
                {i > 0 ? <br /> : null}
                <Code>{i > 0 ? `&${p}` : p}</Code>
              </>
            )
          })}
        </Box>
      ),
    },
    {
      header: 'Result',
      cell: row => <Code>{row.result}</Code>,
    },
  ]

  const data = [
    {
      usage: '?where[created_at]=2018-02-22T00:00:00Z',
      result: 'created on February 22nd, 2018 at 00:00:00 in UTC',
    },
    {
      usage:
        '?where[created_at][gte]=2018-02-22T00:00:00-05:00&where[created_at][lt]=2018-02-23T00:00:00-05:00&',
      result: 'created on February 22nd, 2018 in EST',
    },
  ]

  return <Table columns={columns} data={data} />
}

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Dates & Times</PageHeading>

      <Text>
        All dates and times conform to the{' '}
        <Link to="https://en.wikipedia.org/wiki/ISO_8601" external>
          ISO 8601
        </Link>{' '}
        standard and look like this:
      </Text>
      <ValuesTable />

      <Heading anchored id="responses" size={1} level={2}>
        Responses
      </Heading>

      <Text>Times are always returned in UTC.</Text>

      <Heading anchored id="querying" size={1} level={2}>
        Querying
      </Heading>

      <Heading anchored id="querying-operators" level={3}>
        Operators
      </Heading>

      <Text>
        When querying for <Code>date_time</Code> attributes, multiple operators
        are available. If you want to query{' '}
        <Link to="/apps/services/latest/vertices/plan#url-parameters">
          Plans
        </Link>{' '}
        on <Code>created_at</Code> for example:
      </Text>

      <OperatorsTable />

      <Text>
        <Code>gt</Code>, <Code>gte</Code>, <Code>lt</Code>, and <Code>lte</Code>{' '}
        can be combined to further filter records. For example{' '}
        <Code>
          ?where[created_at][gte]=2018-02-01&where[created_at][lte]=2018-02-28
        </Code>{' '}
        will return all{' '}
        <Link to="/apps/services/latest/vertices/plan#url-parameters">
          Plans
        </Link>{' '}
        created during February 2018.
      </Text>

      <Heading anchored id="querying-time-zone-aware-dates" level={3}>
        Time-zone-aware dates
      </Heading>

      <Text>
        When querying for <Code>date_time</Code> attributes, you can specify a
        date value or a date and time value. Date values are assumed to be in
        the Organization's time zone, and the API will automatically convert
        them to the corresponding moment or span. For example:
      </Text>

      <ConversionsTable />

      <Text>
        You can opt out of this behavior by specifying date and time values. For
        example:
      </Text>

      <ExplicitTimesTable />
    </StackView>
  )
}

export default page

function Link({ external, ...props }) {
  return external ? (
    <SharedLink external {...props} />
  ) : (
    <ReactRouterLink component={SharedLink} {...props} />
  )
}
